import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function ScientificNotationConverter() {
  const [decimal, setDecimal] = useState("");
  const [coefficient, setCoefficient] = useState("");
  const [exponent, setExponent] = useState("");

  const toScientific = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num) || num === 0) return null;

    const exp = Math.floor(Math.log10(Math.abs(num)));
    const coef = num / Math.pow(10, exp);
    
    return { coefficient: coef, exponent: exp };
  };

  const toDecimal = (coef: string, exp: string) => {
    const c = parseFloat(coef);
    const e = parseInt(exp);
    
    if (isNaN(c) || isNaN(e)) return null;
    
    return c * Math.pow(10, e);
  };

  const scientificResult = decimal ? toScientific(decimal) : null;
  const decimalResult = (coefficient && exponent) ? toDecimal(coefficient, exponent) : null;

  return (
    <PageLayout calculatorName="Scientific Notation Converter" calculatorPath="/calculator/scientific-notation">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Scientific Notation</CardTitle>
            <CardDescription>
              Format: a × 10^n where 1 ≤ |a| &lt; 10
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="toScientific" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="toScientific">To Scientific</TabsTrigger>
                <TabsTrigger value="toDecimal">To Decimal</TabsTrigger>
              </TabsList>

              <TabsContent value="toScientific" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="decimal">Decimal Number</Label>
                  <Input
                    id="decimal"
                    type="text"
                    placeholder="e.g., 12345 or 0.00678"
                    value={decimal}
                    onChange={(e) => setDecimal(e.target.value)}
                  />
                </div>

                {scientificResult && (
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Scientific Notation</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-start justify-between gap-4">
                        <p className="text-3xl font-bold text-primary break-all">
                          {scientificResult.coefficient.toFixed(4)} × 10^{scientificResult.exponent}
                        </p>
                        <CopyResultButton value={`${scientificResult.coefficient.toFixed(4)} × 10^${scientificResult.exponent}`} />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Coefficient: {scientificResult.coefficient.toFixed(4)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Exponent: {scientificResult.exponent}
                      </p>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Examples</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-muted-foreground">
                    <p>• 12,345 = 1.2345 × 10^4</p>
                    <p>• 0.00678 = 6.78 × 10^-3</p>
                    <p>• 6,020,000,000 = 6.02 × 10^9 (Avogadro's number)</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="toDecimal" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="coefficient">Coefficient (a)</Label>
                    <Input
                      id="coefficient"
                      type="number"
                      step="any"
                      placeholder="e.g., 6.022"
                      value={coefficient}
                      onChange={(e) => setCoefficient(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="exponent">Exponent (n)</Label>
                    <Input
                      id="exponent"
                      type="number"
                      placeholder="e.g., 23"
                      value={exponent}
                      onChange={(e) => setExponent(e.target.value)}
                    />
                  </div>
                </div>

                {decimalResult !== null && (
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Decimal Form</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-3xl font-bold text-primary break-all overflow-wrap-anywhere">
                            {decimalResult.toExponential()}
                          </p>
                          <p className="text-sm text-muted-foreground mt-2 break-all overflow-wrap-anywhere">
                            Standard form: {decimalResult.toLocaleString('en-US', { maximumFractionDigits: 10 })}
                          </p>
                        </div>
                        <CopyResultButton value={decimalResult.toExponential()} />
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Examples</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-muted-foreground">
                    <p>• 1.234 × 10^4 = 12,340</p>
                    <p>• 5.67 × 10^-3 = 0.00567</p>
                    <p>• 3.0 × 10^8 = 300,000,000 (speed of light in m/s)</p>
                    <p className="text-amber-600 dark:text-amber-500 font-medium">⚠ Note: Exponents ≥308 will return Infinity due to JavaScript limits</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
